import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {formatDate} from 'common/formatters/date-formatter';
import {formatNumber} from 'common/formatters/number-formatter';
import SvgSymbol from 'common/components/svg-symbol';
import {
  DateTimeFormat,
  ReportDifferenceColorEnum,
  ReportingPeriodEnum,
  TimeResolutionEnum,
} from 'common/proxies/enum-proxy';
import {toOrdinal} from 'common/utils/number-util';
import {
  formatUnitsJSX,
  formatNormalizerUnit,
  getIsCurrency,
} from 'common/utils/units-util';
import './index.scss';

export default class SummaryFigure extends React.Component {
  static propTypes = {
    currency: PropTypes.string,
    differenceColor: PropTypes.object,
    icon: PropTypes.string,
    iconUseImg: PropTypes.bool,
    isDifference: PropTypes.bool,
    normalizerUnit: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    period: PropTypes.object,
    resolution: PropTypes.object,
    resource: PropTypes.object,
    timestamp: PropTypes.string,
    title: PropTypes.string.isRequired,
    units: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };

  static defaultProps = {
    iconUseImg: false,
    isDifference: false,
    title: '',
  };

  getArrowUrl(isDifference, value, resource, differenceColor) {
    if (!isDifference) {
      return '';
    }

    const arrows = {
      consumptionIncrease: '/static/icons/common/up.svg',
      consumptionDecrease: '/static/icons/common/down.svg',
      productionIncrease: '/static/icons/common/up-green.svg',
      productionDecrease: '/static/icons/common/down-red.svg',
    };

    // Arrows differ for meters that track production, or if configured
    // to do so
    let reverseArrowColors =
      resource &&
      resource.type &&
      (resource.type === 'production' || resource.type === 'recycling');
    if (differenceColor) {
      reverseArrowColors =
        differenceColor.id === ReportDifferenceColorEnum.GREEN_INC__RED_DEC.id;
    }
    if (reverseArrowColors) {
      // Upward red arrow for increase, downward green arrow for decrease.
      return value < 0 ? arrows.productionDecrease : arrows.productionIncrease;
    }

    // Upward red arrow for increase, downward green arrow for decrease.
    return value < 0 ? arrows.consumptionDecrease : arrows.consumptionIncrease;
  }

  getFormattedPeriod(timestamp, period, resolution) {
    const periodId = parseInt(period.id, 10);
    const periodFormats = {
      dayOfMonth: [
        ReportingPeriodEnum.MONTH.id,
        ReportingPeriodEnum.LAST_MONTH.id,
        ReportingPeriodEnum.LAST_THIRTY_DAYS.id,
        ReportingPeriodEnum.LAST_FOUR_WEEKS.id,
      ],
      dayOfWeek: [
        ReportingPeriodEnum.WEEK.id,
        ReportingPeriodEnum.LAST_WEEK.id,
        ReportingPeriodEnum.LAST_SEVEN_DAYS.id,
      ],
      time: [
        ReportingPeriodEnum.TODAY.id,
        ReportingPeriodEnum.YESTERDAY.id,
        ReportingPeriodEnum.LAST_TWENTY_FOUR_HOURS.id,
        ReportingPeriodEnum.LAST_FOURTY_EIGHT_HOURS.id,
      ],
    };

    // custom periods use the resolution to determine format
    const resolutionId = parseInt(resolution && resolution.id, 10);
    const resolutionFormats = {
      [TimeResolutionEnum.LIVE.id]: DateTimeFormat.TIME,
      [TimeResolutionEnum.QUARTER_HOUR.id]: DateTimeFormat.TIME,
      [TimeResolutionEnum.HOUR.id]: DateTimeFormat.TIME,
      [TimeResolutionEnum.DAY.id]: DateTimeFormat.TEXTUAL_MONTH_DAY,
      [TimeResolutionEnum.MONTH.id]: DateTimeFormat.MONTH,
      [TimeResolutionEnum.YEAR.id]: DateTimeFormat.YEAR,
    };

    // default to month
    let format = DateTimeFormat.MONTH;
    if (periodId) {
      if (periodFormats.time.indexOf(periodId) > -1) {
        format = DateTimeFormat.TIME;
      } else if (periodFormats.dayOfWeek.indexOf(periodId) > -1) {
        format = DateTimeFormat.DAY_OF_WEEK;
      } else if (periodFormats.dayOfMonth.indexOf(periodId) > -1) {
        format = DateTimeFormat.DAY;
      }
    } else if (resolutionId && resolutionId in resolutionFormats) {
      // handle custom periods by checking the resolution
      format = resolutionFormats[resolutionId];
    }

    let formatted = formatDate(moment.utc(timestamp), format);
    if (!isNaN(formatted) && resolutionId != TimeResolutionEnum.YEAR.id) {
      formatted = toOrdinal(formatted);
    }
    return `(${formatted})`;
  }

  render() {
    const {
      differenceColor,
      icon,
      iconUseImg,
      isDifference,
      normalizerUnit,
      period,
      resource,
      resolution,
      timestamp,
      title,
      units,
      value,
    } = this.props;

    const summaryFigureValue = isDifference && value < 0 ? -value : value;
    const arrowUrl = this.getArrowUrl(
      isDifference,
      value,
      resource,
      differenceColor,
    );

    // If currency is present, use that instead of units.
    const shouldFormatAsCurrency = getIsCurrency(units);
    const formattedValue = shouldFormatAsCurrency
      ? formatNumber(summaryFigureValue, 'currency', null, 'USD')
      : formatNumber(summaryFigureValue);
    const formattedUnits = shouldFormatAsCurrency
      ? null
      : formatUnitsJSX(units);

    let formattedPeriod;
    if (timestamp && (period || resolution)) {
      formattedPeriod = this.getFormattedPeriod(timestamp, period, resolution);
    }

    return (
      <div
        className={'SummaryFigure'}
        style={{
          borderLeftWidth: icon ? null : 4,
        }}>
        {icon ? (
          <div className={'SummaryFigure-icon'}>
            <SvgSymbol name={icon} useImg={iconUseImg} />
          </div>
        ) : null}

        <div className={'SummaryFigure-title'}>
          {title.toString().replace('&ndash;', '\u2013')}
        </div>

        <div className={'SummaryFigure-content'}>
          {arrowUrl ? (
            <span className={'SummaryFigure-arrow'}>
              <img src={arrowUrl} />
            </span>
          ) : null}

          <span className={'SummaryFigure-value'}>{formattedValue}</span>

          {formattedUnits ? (
            <span className={'SummaryFigure-units'}>{formattedUnits}</span>
          ) : null}

          {normalizerUnit && !isDifference ? (
            <span className={'SummaryFigure-units'}>
              {formatNormalizerUnit(normalizerUnit)}
            </span>
          ) : null}

          {formattedPeriod ? (
            <span className={'SummaryFigure-period'}>{formattedPeriod}</span>
          ) : null}
        </div>
      </div>
    );
  }
}
